import type { EnvironmentDefinition, EnvironmentView, ScenePlan } from "@pushback-and-chaos/contracts";

/**
 * PLAN PLAYBACK (ADR 0014, commitment 6). The client plays a ScenePlan it
 * was handed; it never derives one. The section is drawn at the new view;
 * for every actor with a travel step this module sets a `data-offset-x/y`
 * that carries the figure back along its path and decays to zero at the
 * destination, plus an opacity for appearances and vanishings. Attributes
 * only — the ambient loop in animate.ts sums the offset into its own
 * transform, so the two never fight. Slot positions come from the
 * composer's invisible slot markers; playback needs no geometry of its own.
 */

/**
 * The view the plan plays over: the new view plus anyone who left, still
 * drawn at the exit they vanished from, so the walk-out is seen before
 * they are gone. Presentation only; the true view renders when the plan
 * finishes, and the two differ only by the figures that have vanished.
 */
export function transitionViewFor(definition: EnvironmentDefinition, previous: EnvironmentView, next: EnvironmentView, plan: ScenePlan): EnvironmentView {
  const actors = [...next.actors];
  for (const step of plan.steps) {
    if (step.kind !== "vanish") continue;
    const before = previous.actors.find((a) => a.instanceId === step.actor);
    if (before === undefined || actors.some((a) => a.instanceId === step.actor)) continue;
    const slot = Math.max(0, definition.rooms.find((r) => r.id === step.at.room)?.slots.findIndex((s) => s.id === step.at.slot) ?? 0);
    actors.push({ ...before, room: step.at.room, slot, pose: "stand" });
  }
  return { ...next, actors };
}

interface Point {
  readonly x: number;
  readonly y: number;
}

interface Travel {
  readonly path: readonly Point[];
  readonly lengths: readonly number[];
  readonly start: number;
  readonly end: number;
}

interface Track {
  readonly travels: Travel[];
  appearAt: number | null;
  vanishAt: number | null;
}

const VANISH_MS = 500;

function positionAt(travel: Travel, t: number): Point {
  const total = travel.lengths.reduce((a, b) => a + b, 0);
  const frac = travel.end === travel.start ? 1 : Math.min(Math.max((t - travel.start) / (travel.end - travel.start), 0), 1);
  let remaining = frac * total;
  for (let i = 0; i < travel.lengths.length; i += 1) {
    const length = travel.lengths[i] ?? 0;
    const from = travel.path[i] ?? { x: 0, y: 0 };
    const to = travel.path[i + 1] ?? from;
    if (remaining <= length || i === travel.lengths.length - 1) {
      const k = length === 0 ? 1 : Math.min(remaining / length, 1);
      return { x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k };
    }
    remaining -= length;
  }
  return travel.path[travel.path.length - 1] ?? { x: 0, y: 0 };
}

/**
 * Plays `plan` over the SVG inside `container`. `speed` compresses the
 * planner's physical durations (a walk across the floor is twenty seconds
 * at 1×; play uses 3×). Returns a cancel function; calls `onDone` when the
 * last step has played.
 */
export function playPlan(container: HTMLElement, plan: ScenePlan, speed: number, onDone: () => void): () => void {
  const root = container.querySelector("svg");
  if (root === null || plan.steps.length === 0) {
    onDone();
    return () => undefined;
  }
  const slots = new Map<string, Point>();
  root.querySelectorAll<SVGCircleElement>("[data-slot]").forEach((c) => {
    slots.set(c.getAttribute("data-slot") ?? "", { x: Number(c.getAttribute("cx")), y: Number(c.getAttribute("cy")) });
  });
  const at = (room: string, slot: string): Point => slots.get(`${room}/${slot}`) ?? { x: 0, y: 0 };

  const tracks = new Map<string, Track>();
  const cursor = new Map<string, number>();
  let last = 0;
  for (const step of plan.steps) {
    if (step.kind === "fixture" || step.kind === "ink") continue;
    const track = tracks.get(step.actor) ?? { travels: [], appearAt: null, vanishAt: null };
    tracks.set(step.actor, track);
    const t0 = cursor.get(step.actor) ?? 0;
    let t1 = t0;
    if (step.kind === "travel") {
      const path = step.path.map((p) => at(p.room, p.slot));
      const lengths = path.slice(1).map((p, i) => Math.hypot(p.x - (path[i]?.x ?? 0), p.y - (path[i]?.y ?? 0)));
      t1 = t0 + step.durationMs / speed;
      track.travels.push({ path, lengths, start: t0, end: t1 });
    } else if (step.kind === "pose") {
      t1 = t0 + step.durationMs / speed;
    } else if (step.kind === "appear") {
      track.appearAt = t0;
    } else if (step.kind === "vanish") {
      track.vanishAt = t0;
      t1 = t0 + VANISH_MS;
    }
    cursor.set(step.actor, t1);
    last = Math.max(last, t1);
  }

  const elementsFor = (actor: string): SVGElement[] => [
    ...root.querySelectorAll<SVGElement>(`[data-actor="${actor}"]`),
    ...root.querySelectorAll<SVGElement>(`[data-companion^="${actor}-"]`),
  ];
  const mirrored = (element: SVGElement): boolean => (element.parentElement?.getAttribute("transform") ?? "").includes("scale(-1");

  const began = performance.now();
  let raf = 0;
  const frame = (now: number): void => {
    const t = now - began;
    for (const [actor, track] of tracks) {
      const elements = elementsFor(actor);
      if (elements.length === 0) continue;
      const finalTravel = track.travels[track.travels.length - 1];
      const destination = finalTravel?.path[finalTravel.path.length - 1] ?? null;
      let offset: Point = { x: 0, y: 0 };
      let travelling = false;
      if (destination !== null) {
        let position = track.travels[0]?.path[0] ?? destination;
        for (const travel of track.travels) {
          if (t >= travel.end) position = travel.path[travel.path.length - 1] ?? position;
          else if (t >= travel.start) {
            position = positionAt(travel, t);
            travelling = true;
            break;
          } else break;
        }
        offset = { x: position.x - destination.x, y: position.y - destination.y };
      }
      let opacity = 1;
      if (track.appearAt !== null && t < track.appearAt) opacity = 0;
      if (track.vanishAt !== null && t >= track.vanishAt) opacity = Math.max(0, 1 - (t - track.vanishAt) / VANISH_MS);
      for (const element of elements) {
        const ox = mirrored(element) ? -offset.x : offset.x;
        const rx = Math.round(ox * 100) / 100;
        const ry = Math.round(offset.y * 100) / 100;
        if (element.hasAttribute("data-actor")) {
          element.setAttribute("data-offset-x", String(rx));
          element.setAttribute("data-offset-y", String(ry));
          if (travelling) element.setAttribute("data-travelling", "1");
          else element.removeAttribute("data-travelling");
        } else {
          element.setAttribute("transform", `translate(${String(rx)},${String(ry)})`);
        }
        element.setAttribute("opacity", String(Math.round(opacity * 100) / 100));
      }
    }
    if (t < last + 200) raf = requestAnimationFrame(frame);
    else onDone();
  };
  raf = requestAnimationFrame(frame);
  return () => cancelAnimationFrame(raf);
}
